import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useCreateKudos } from "@/hooks/use-kudos";
import { FormError } from "@/components/shared/form-error";

const createKudosSchema = z.object({
  message: z.string().min(1, "Message is required").max(2000),
});

type CreateKudosForm = z.infer<typeof createKudosSchema>;

interface CreateKudosDialogProps {
  objectiveId?: string;
  krId?: string;
}

export function CreateKudosDialog({ objectiveId, krId }: CreateKudosDialogProps) {
  const [open, setOpen] = useState(false);
  const createKudos = useCreateKudos();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateKudosForm>({
    resolver: zodResolver(createKudosSchema),
    defaultValues: { message: "" },
  });

  const onSubmit = (data: CreateKudosForm) => {
    createKudos.mutate(
      {
        message: data.message,
        objective_id: objectiveId ?? null,
        kr_id: krId ?? null,
      },
      {
        onSuccess: () => {
          setOpen(false);
          reset();
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          Give Kudos
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Give Kudos</DialogTitle>
          <DialogDescription>
            Recognize great work with a kudos message.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="kudos-message">Message</Label>
            <Textarea
              id="kudos-message"
              placeholder="Great work on..."
              rows={4}
              {...register("message")}
            />
            <FormError message={errors.message?.message} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={createKudos.isPending}>
              {createKudos.isPending ? "Sending..." : "Give Kudos"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
